import { Box, Button, Typography } from "@outschool/backpack";
import { Trans, useTranslation } from "@outschool/localization";
import React from "react";

import * as Env from "../../../shared/Env";
import {
  useCreateSetupIntentWithPaymentElement,
  usePaymentMethods
} from "../../hooks/CurrentUser";
import ErrorMessage from "../ErrorMessage";
import LoadingWithMessage from "../LoadingWithMessage";
import {
  BillingDetails,
  StripeProvider,
  useDefaultStripeErrorMessage
} from "../payments/Stripe";
import StripeNewCard from "../payments/StripeNewCard";

type PaymentMethodModalContentProps = {
  onClose?: () => void;
};

function EsaMembershipSavePaymentMethodContent({
  onClose = () => {}
}: PaymentMethodModalContentProps) {
  const { t } = useTranslation(
    "client\\components\\esaMemberships\\EsaMembershipSavePaymentMethod"
  );
  const [isComplete, setIsComplete] = React.useState(false);
  const errorMessage = useDefaultStripeErrorMessage();

  const {
    loading: paymentMethodsLoading,
    error: paymentMethodsError,
    paymentMethods
  } = usePaymentMethods();
  const [defaultPaymentMethod] = paymentMethods;

  const [createSetupIntentWithPaymentElement, { error, loading }] =
    useCreateSetupIntentWithPaymentElement(defaultPaymentMethod?.uid);

  const handleSaveCard = React.useCallback(async () => {
    await createSetupIntentWithPaymentElement();
    onClose();
  }, [createSetupIntentWithPaymentElement, onClose]);

  return (
    <Box>
      <Typography
        variant="h3"
        sx={{
          paddingBottom: "0.5em"
        }}
      >
        {t("Add a Backup Payment Method")}{" "}
      </Typography>
      <Trans t={t}>
        This class is{" "}
        <Typography style={{ fontWeight: "fontWeightBold" }}>
          charged weekly
        </Typography>
        . When your credits run out, we’ll charge your backup payment method to
        make sure you don’t miss any classes.
      </Trans>
      <Box
        sx={{
          paddingTop: "0.5em"
        }}
      >
        {paymentMethodsError ? (
          <ErrorMessage
            value={errorMessage}
            showStatusPageLink={Env.IS_READ_ONLY_MODE}
          />
        ) : paymentMethodsLoading ? (
          <LoadingWithMessage>
            {t`Please wait while we load your payment methods`}
          </LoadingWithMessage>
        ) : (
          <>
            <Box
              sx={{
                paddingBottom: "1em"
              }}
            >
              <StripeNewCard
                setIsCardComplete={setIsComplete}
                setBillingDetails={(_billingDetails: BillingDetails) => {}}
              />
            </Box>
            {loading ? (
              <LoadingWithMessage>
                {t`Please wait while we save your credit card`}
              </LoadingWithMessage>
            ) : (
              <>
                <ErrorMessage
                  value={error && error.message}
                  showStatusPageLink={Env.IS_READ_ONLY_MODE}
                />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Button
                    variant="link"
                    onClick={onClose}
                    sx={{
                      lineHeight: 1.5
                    }}
                    disabled={loading}
                  >
                    {t("Skip for now")}
                  </Button>
                  <Button
                    variant="contained"
                    disabled={!isComplete || loading}
                    onClick={handleSaveCard}
                  >
                    {t`Save Card`}
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

export function EsaMembershipSavePaymentMethod(
  props: PaymentMethodModalContentProps
) {
  return (
    <StripeProvider amountInCents={0}>
      <EsaMembershipSavePaymentMethodContent {...props} />
    </StripeProvider>
  );
}
